"use client";

import { MediaGallery, GalleryMediaCategory, GalleryMediaItem, useMediaGallery } from "@/components/common/gallery";
import { GalleryCategories, GalleryMediaCounter, GalleryNavigationButtons } from "@/components/common/gallery/overlays";
import { prepareGalleryMedia } from "@/utils/sup/gallery";
import * as React from "react";
import { type GalleryProps, getSortedCategories } from "./shared";

// Re-export GalleryProps for external consumers
export type { GalleryProps };

// Internal component that uses gallery context to sync category
function GalleryContent({
	media,
	sortedCategories,
	selectedCategory,
	setSelectedCategory,
}: {
	media: GalleryMediaItem[];
	sortedCategories: GalleryMediaCategory[];
	selectedCategory: GalleryMediaCategory | undefined;
	setSelectedCategory: (category: GalleryMediaCategory | undefined) => void;
}) {
	const { currentIndex, isFullscreen, goToPrevious, goToNext } = useMediaGallery();

	// Sync selected category with current media item
	React.useEffect(() => {
		const currentItem = media[currentIndex];
		if (currentItem?.space) {
			const currentCategory = sortedCategories.find(cat => cat.code === currentItem.space);
			if (currentCategory && currentCategory.code !== selectedCategory?.code) {
				setSelectedCategory(currentCategory);
			}
		}
	}, [currentIndex, media, sortedCategories, selectedCategory, setSelectedCategory]);

	const showCategories = isFullscreen && sortedCategories.length > 0;
	const canGoPrevious = currentIndex > 0;
	const canGoNext = currentIndex < media.length - 1;

	return (
		<>
			{/* Custom Media Counter - hidden when category nav is visible */}
			{!showCategories && (
				<GalleryMediaCounter
					className="opacity-100 md:opacity-0 md:group-hover/card:opacity-100 transition-opacity duration-200"
					showMediaName={true}
				/>
			)}

			{/* Navigation Buttons */}
			<GalleryNavigationButtons
				onPrevious={goToPrevious}
				onNext={goToNext}
				canGoPrevious={canGoPrevious}
				canGoNext={canGoNext}
			/>

			{/* Category Navigation - Only show in fullscreen mode */}
			{showCategories && (
				<GalleryCategories
					categories={sortedCategories}
					selectedCategory={selectedCategory}
					onCategoryChange={setSelectedCategory}
				/>
			)}
		</>
	);
}

export const GallerySmall = React.memo(function Gallery({
	coverImage,
	mediaList = [],
	type,
	projectId,
	className,
	isLoading = false,
	shouldPrioritizeCoverImage = true,
	sizes = "(max-width: 480px) 40vw, (max-width: 768px) 30vw, 320px",
	onMainImageClick,
	categories,
	aspectRatio = "default",
	imageAlignment = "center",
	darkerBackground = false,
	videoAutoplayOnLoad = false,
}: GalleryProps) {
	// Create the media list starting with cover image, then mediaList
	const media: GalleryMediaItem[] = React.useMemo(() => {
		return prepareGalleryMedia({
			coverImage,
			mediaList,
			projectId,
			type,
			shouldPrioritizeCoverImage,
		});
	}, [coverImage, mediaList, projectId, type, shouldPrioritizeCoverImage]);

	// Sort categories by unitSpaces priority and filter to only those with media
	const sortedCategories = React.useMemo(() => {
		return getSortedCategories(categories, media);
	}, [categories, media]);

	// Track selected category - select the first category from sorted list
	const [selectedCategory, setSelectedCategory] = React.useState<GalleryMediaCategory | undefined>(sortedCategories[0]);

	if (isLoading) {
		return (
			<div className="w-full h-full bg-gray-100 animate-pulse">
				<div className="w-full h-full bg-gray-200"></div>
			</div>
		);
	}

	// Determine aspect ratio classes
	const aspectRatioClasses =
		aspectRatio === "square"
			? "aspect-square"
			: aspectRatio === "fixed-height"
				? "aspect-square @4xl:aspect-auto @4xl:min-h-[500px]"
				: ""; // default: no constraint, inherits from wrapper completely

	return (
		<MediaGallery
			media={media}
			className={`w-full h-full ${aspectRatioClasses} ${className}`}
			showNavigationButtons={media.length > 1}
			showMediaCounter={false} // We'll use custom counter
			enableKeyboardNavigation={true}
			enableDoubleTapFullscreen={true}
			autoLoop={true}
			videoHideControls={true}
			videoAutoplayOnLoad={videoAutoplayOnLoad}
			// for modern devices (retina/mobile), it will automaticaly download image of 2x or 3x size
			sizes={sizes}
			onMainImageClick={onMainImageClick}
			imageAlignment={imageAlignment}
			darkerBackground={darkerBackground}
		>
			<GalleryContent
				media={media}
				sortedCategories={sortedCategories}
				selectedCategory={selectedCategory}
				setSelectedCategory={setSelectedCategory}
			/>
		</MediaGallery>
	);
});
